<script setup lang="ts">
import { computed, ref, watch } from "vue";
import { useRouter } from "vue-router";
import { useI18n } from "vue-i18n";
import Modal from "@/ui/components/modal.vue";
import LocaleNumberInput from "@/ui/components/LocaleNumberInput.vue";
import { useApiClient } from "@/api/client";
import HierarchyDefinitionDO = App.Data.Hierarchy.HierarchyDefinitionDO;
import Currency = Modules.BasePrice.Data.Currency;
import PricingModel = Modules.BasePrice.Data.PricingModel;

const { t } = useI18n();
const router = useRouter();
const visible = defineModel<boolean>("visible", { default: false });

/** Which kind of plan the overview is showing — a bruto plan has no target. */
const { pricingModel = "netto" } = defineProps<{
	pricingModel?: PricingModel;
}>();

const isBruto = computed(() => pricingModel === "bruto");

const name = ref("");
const year = ref<number>(new Date().getFullYear() + 1);
const hierarchyDefinitionId = ref<number | null>(null);
const target = ref<number>(0);

/**
 * Not offered as a choice — every plan is priced in euros. Still sent, because
 * the column and the API both carry it, so the day a second currency is real
 * this is a field to surface rather than a value to thread through.
 */
const currency: Currency = "EUR";

const definitions = ref<HierarchyDefinitionDO[]>([]);
const loadingDefinitions = ref(false);
const creating = ref(false);

/**
 * Screen-level hierarchies only, decided server side — a base price is a price
 * per thousand impressions, and only screens carry impressions.
 */
const loadDefinitions = async () => {
	loadingDefinitions.value = true;

	const response = await useApiClient("basePrice").getHierarchyDefinitions();
	if (response.isOk()) {
		definitions.value = response.value;

		// One choice is not a choice; pre-select it rather than making it a step.
		if (definitions.value.length === 1) {
			hierarchyDefinitionId.value = definitions.value[0].id;
		}
	}

	loadingDefinitions.value = false;
};

const reset = () => {
	name.value = "";
	year.value = new Date().getFullYear() + 1;
	hierarchyDefinitionId.value = null;
	target.value = 0;
};

watch(visible, (open) => {
	if (!open) return;

	reset();
	loadDefinitions();
});

const canCreate = computed(
	() =>
		name.value.trim().length > 0 &&
		hierarchyDefinitionId.value !== null &&
		year.value > 0 &&
		(isBruto.value || target.value > 0)
);

/** Opens the new plan straight away: an empty plan is only useful once configured. */
const create = async () => {
	if (!canCreate.value) return;

	creating.value = true;

	const response = await useApiClient("basePrice").createPlan({
		year: year.value,
		name: name.value.trim(),
		hierarchy_definition_id: hierarchyDefinitionId.value!,
		...(isBruto.value ? {} : { target: target.value }),
		currency,
		pricing_model: pricingModel,
	});

	creating.value = false;

	if (!response.isOk()) return;

	visible.value = false;
	router.push(
		isBruto.value
			? `/base-prices/${response.value.uuid}/bruto`
			: `/base-prices/${response.value.uuid}/edit`
	);
};
</script>

<template>
	<modal
		v-model:visible="visible"
		:title="
			isBruto ? t('base_price.bruto.create_title') : 'New base price plan'
		"
		icon="fa-light fa-file-plus"
		width="40rem"
		:persistent="false"
	>
		<v-text-field
			v-model="name"
			label="Name"
			density="compact"
			variant="filled"
			hide-details="auto"
			class="mb-4"
			autofocus
		/>

		<v-select
			v-model="hierarchyDefinitionId"
			:items="definitions"
			:loading="loadingDefinitions"
			item-title="name"
			item-value="id"
			label="Hierarchy"
			density="compact"
			variant="filled"
			hide-details="auto"
			class="mb-4"
			:no-data-text="
				loadingDefinitions ? 'Loading…' : 'No screen hierarchies available'
			"
		/>

		<div class="d-flex ga-4 flex-wrap">
			<v-number-input
				v-model="year"
				control-variant="stacked"
				:min="2000"
				:max="2100"
				:step="1"
				density="compact"
				variant="filled"
				hide-details="auto"
				style="width: 140px"
			/>

			<div v-if="!isBruto" class="flex-grow-1" style="min-width: 180px">
				<locale-number-input
					v-model="target"
					format="money_no_fraction"
					density="compact"
					variant="filled"
					hide-details="auto"
				/>
			</div>
		</div>

		<template v-slot:actions>
			<v-spacer />
			<v-btn variant="text" color="error" @click="visible = false"
				>Cancel</v-btn
			>
			<v-btn
				color="primary"
				variant="tonal"
				:loading="creating"
				:disabled="!canCreate"
				@click="create"
			>
				Create plan
			</v-btn>
		</template>
	</modal>
</template>
